// Product gallery filtering, like the live site. Vanilla, deferred, no dependencies.
// - hides "Swatch …" images (they only feed the color swatches)
// - shows the selected color's images (image alt text = color name) plus images whose alt is the
//   product title (shared shots like "Benefits of Bamboo"); shows everything if nothing matches
// - desktop thumbnail rail scrolls the stacked images instead of sliding
// - puts the product badges (status + Customer Favorite) on the first visible photo
// Horizon replaces the whole <media-gallery> on every variant change, so this re-runs on each new one.
(() => {
  const HIDDEN = 'sivana-media-hidden';
  const norm = (value) => (value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  function selectedColor(section) {
    const fieldset = [...section.querySelectorAll('variant-picker fieldset.variant-option')].find((element) =>
      /-colou?r$/.test(element.dataset.optionId || '')
    );
    const checked = fieldset?.querySelector('input:checked');
    if (checked) return norm(checked.value);
    // single-value color options render as plain text
    const single = [...section.querySelectorAll('variant-picker [data-testid="variant-option-single"]')].find((element) =>
      /^colou?r/i.test(element.textContent.trim())
    );
    return norm(single?.querySelector('[data-testid="variant-option-single-value"]')?.textContent);
  }

  function productTitle(section) {
    const picker = section.querySelector('variant-picker[data-product-title]');
    if (picker) return norm(picker.dataset.productTitle);
    const heading = section.querySelector('.sivana-pdp-title__heading');
    return norm(heading?.firstChild?.textContent);
  }

  function altOf(element) {
    return norm(element.querySelector('img')?.getAttribute('alt'));
  }

  function filter(gallery) {
    const section = gallery.closest('.shopify-section');
    if (!section) return;
    const color = selectedColor(section);
    const title = productTitle(section);
    const key = `${color}|${title}`;
    if (gallery.dataset.sivanaFiltered === key) return;
    gallery.dataset.sivanaFiltered = key;

    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const alts = slides.map(altOf);
    const isSwatch = alts.map((alt) => alt.includes('swatch'));
    let show = alts.map((alt, index) => !isSwatch[index] && alt !== '' && (alt === color || alt === title));
    if (!show.some(Boolean)) show = isSwatch.map((swatch) => !swatch);

    slides.forEach((slide, index) => slide.classList.toggle(HIDDEN, !show[index]));
    gallery.querySelectorAll('.media-gallery__grid > li').forEach((item, index) => {
      item.classList.toggle(HIDDEN, !show[index]);
    });
    gallery.querySelectorAll('slideshow-controls').forEach((controls) => {
      controls.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb, index) => {
        thumb.classList.toggle(HIDDEN, !show[index]);
      });
    });

    // Bring the first visible image into view in the mobile slideshow.
    const scroller = gallery.querySelector('slideshow-slides');
    const first = slides.find((slide, index) => show[index]);
    if (scroller && first && scroller.scrollWidth > scroller.clientWidth) {
      scroller.scrollLeft = first.offsetLeft - scroller.offsetLeft;
    }

    placeBadges(section, gallery);
  }

  // Badges (snippets/sivana-badges.liquid via the title block) go on the first visible photo of both
  // layouts: the desktop grid and the phone slideshow. Only one layout shows at a time.
  function placeBadges(section, gallery) {
    gallery.querySelectorAll('.sivana-gallery-badges').forEach((element) => element.remove());
    const source = section.querySelector('.sivana-pdp-title [data-sivana-gallery-badges]');
    if (!source || !source.querySelector('.sivana-badge')) return;
    [
      gallery.querySelector(`.media-gallery__grid > li:not(.${HIDDEN})`),
      gallery.querySelector(`slideshow-slides > slideshow-slide:not(.${HIDDEN})`),
    ]
      .filter(Boolean)
      .forEach((target) => {
        const badges = source.cloneNode(true);
        badges.hidden = false;
        badges.removeAttribute('data-sivana-gallery-badges');
        target.classList.add('sivana-has-badges');
        target.prepend(badges);
      });
  }

  // Desktop: the rail scrolls to the stacked image instead of moving a slideshow.
  function onThumbnailClick(event) {
    const thumb = event.target.closest('.slideshow-controls__thumbnail');
    if (!thumb || !window.matchMedia('(min-width: 750px)').matches) return;
    const gallery = thumb.closest('media-gallery.media-gallery--grid');
    if (!gallery) return;
    const thumbs = [...thumb.parentElement.querySelectorAll('.slideshow-controls__thumbnail')];
    const slide = gallery.querySelectorAll('.media-gallery__grid > li')[thumbs.indexOf(thumb)];
    if (!slide) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const top = slide.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  function run() {
    document.querySelectorAll('.product-information__media media-gallery').forEach(filter);
  }

  function init() {
    run();
    document.addEventListener('click', onThumbnailClick, true);
    document.addEventListener('sivana:pdp-refreshed', () => {
      document.querySelectorAll('.product-information__media media-gallery').forEach((gallery) => {
        delete gallery.dataset.sivanaFiltered;
      });
      run();
    });
    document.querySelectorAll('.product-information').forEach((info) => {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          run();
        });
      }).observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked'] });
      info.addEventListener('change', () => requestAnimationFrame(run));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
